var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { loginApi } from "apis/eduidLogin";
import { FRONTEND_ACTION } from "components/Common/MultiFactorAuthentication";
import { useAppDispatch, useAppSelector } from "eduid-hooks";
import { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import loginSlice from "slices/Login";
/* Not exported - keep all use through functions in this module */
const THIS_DEVICE_KEY = "login.device";
const REMEMBER_ME_KEY = "login.remember_me";
/**
 * Fetch the "new device" endpoint from the backend, and store it in local storage.
 */
export function NewDevice() {
    const dispatch = useAppDispatch();
    const ref = useAppSelector((state) => state.login.ref);
    const [fetchNewDevice] = loginApi.useLazyFetchNewDeviceQuery();
    useEffect(() => {
        function getKnownDevice() {
            return __awaiter(this, void 0, void 0, function* () {
                if (ref) {
                    const response = yield fetchNewDevice({ ref });
                    if (response.isSuccess) {
                        const thisDevice = response.data.payload.new_device;
                        if (globalThis.localStorage) {
                            globalThis.localStorage.setItem(THIS_DEVICE_KEY, thisDevice);
                        }
                        // trigger another request to /next
                        dispatch(loginSlice.actions.callLoginNext());
                    }
                }
            });
        }
        getKnownDevice();
    }, [dispatch, fetchNewDevice, ref]);
    /* For now, we don't render anything here but just prime local storage with a known_device.
     * In a later version, we will likely ask for a username here, and solve a captcha...
     */
    return null;
}
/**
 * Component rendering a checkbox allowing the user to decide if they want to be remembered on this device or not.
 */
export function RememberMeCheckbox() {
    const remember_me = useAppSelector((state) => state.login.remember_me);
    const previous_this_device = useAppSelector((state) => state.login.previous_this_device);
    const next_page = useAppSelector((state) => state.login.next_page);
    const dispatch = useAppDispatch();
    const securityZoneAction = sessionStorage.getItem(FRONTEND_ACTION);
    function handleSwitchChange() {
        const newValue = !remember_me;
        if (globalThis.localStorage) {
            globalThis.localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify(newValue));
        }
        dispatch(loginSlice.actions.setRememberMe(newValue));
        if (newValue && previous_this_device) {
            // if the user toggled 'remember me' to off, and then back to on again, we restore the
            // state.login.this_device from state.login.previous_this_device
            dispatch(loginSlice.actions.addThisDevice(previous_this_device));
        }
        if (!newValue) {
            forgetThisDevice(dispatch);
        }
        // re-fetch '/next' now that the conditions for logging in has changed
        dispatch(loginSlice.actions.callLoginNext());
    }
    if (!globalThis.localStorage || remember_me === undefined) {
        // Might as well not even show the 'remember me' checkbox if there is no local storage.
        // If remember_me is undefined it means the login app hasn't been initialised yet.
        return null;
    }
    if (!next_page ||
        next_page === "TOU" ||
        next_page === "OTHER_DEVICE" ||
        next_page === "FINISHED" ||
        securityZoneAction) {
        // Don't show this component on some screens, or before next_page is initialised
        return null;
    }
    return (_jsx("fieldset", { className: "border-toggle-area", children: _jsxs("label", { className: "toggle", htmlFor: "remember-me", children: [_jsxs("legend", { children: [_jsx(FormattedMessage, { id: "newDevice.remember", defaultMessage: "Remember me on this device", description: "Login remember user device" }), remember_me && (_jsx("p", { className: "help-text", children: _jsx(FormattedMessage, { id: "newDevice.mfa", defaultMessage: "Turning this off will enable login with username and password instead.", description: "MFA as the first step of login, toggle off" }) }))] }), _jsx("input", { onChange: handleSwitchChange, className: "toggle-checkbox", type: "checkbox", checked: remember_me, id: "remember-me" }), _jsx("div", { className: "toggle-switch" })] }) }));
}
/**
 * If this_device and remember_me haven't been set in redux state yet, initialise them from local storage.
 */
export function initKnownDevice(this_device, remember_me, dispatch) {
    if (!globalThis.localStorage) {
        // Can't remember devices if there is no local storage
        dispatch(loginSlice.actions.setRememberMe(false));
        return { init_this_device: undefined, init_remember_me: false };
    }
    if (!this_device) {
        // try to initialise this_device from local storage
        this_device = globalThis.localStorage.getItem(THIS_DEVICE_KEY) || undefined;
        if (this_device) {
            dispatch(loginSlice.actions.addThisDevice(this_device));
        }
    }
    if (remember_me === undefined) {
        // try to initialise remember_me from local storage
        const _remember_me = globalThis.localStorage.getItem(REMEMBER_ME_KEY) || "true";
        remember_me = !!JSON.parse(_remember_me);
        dispatch(loginSlice.actions.setRememberMe(remember_me));
    }
    return { init_this_device: this_device, init_remember_me: remember_me };
}
/**
 * Forget the device identifier in local storage, and retire it in redux state.
 * Retire means that it is retained in memory to allow the user to revert the decision to forget it.
 *
 */
export function forgetThisDevice(dispatch) {
    if (globalThis.localStorage) {
        globalThis.localStorage.removeItem(THIS_DEVICE_KEY);
    }
    dispatch(loginSlice.actions.clearThisDevice());
}
